import React, { useCallback } from 'react';
import { useDrop } from 'react-dnd';
import { 
    FlexDiv, 
    Block,
    Heading4,
    TextDefault,
    ThemedButton
} from '@awarego-ui/components';
import { Card, CardBody } from 'reactstrap';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import BlockRenderer from './BlockRenderer';

const CanvasBlock = ({ 
    block, 
    index, 
    isSelected, 
    onSelect, 
    onUpdate, 
    onRemove, 
    onMove 
}) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'block',
        drop: (item) => {
            if (item.blockType) {
                // Handle dropping a new block
                return;
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }));

    return (
        <div
            ref={drop}
            className={`canvas-block ${isSelected ? 'selected' : ''} ${isOver ? 'drop-target' : ''}`}
            onClick={() => onSelect(block)}
        >
            <Card>
                <CardBody>
                    <FlexDiv alignCenter gap={12}>
                        <DragIndicatorIcon className="drag-handle" />
                        <FlexDiv flexGrow={1}>
                            <BlockRenderer 
                                block={block} 
                                isPreview={true}
                                onUpdate={onUpdate}
                            />
                        </FlexDiv>
                        <FlexDiv gap={8}>
                            <ThemedButton
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(block);
                                }}
                            />
                            <ThemedButton
                                size="small"
                                mode="danger"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(block.id);
                                }}
                            />
                        </FlexDiv>
                    </FlexDiv>
                </CardBody>
            </Card>
        </div>
    );
};

const PresentationArea = ({ 
    presentation, 
    isSelected, 
    onSelect, 
    onUpdate 
}) => {
    if (!presentation) {
        return (
            <div className="presentation-area empty">
                <Card>
                    <CardBody>
                        <FlexDiv column alignCenter gap={16} padding={32}>
                            <TextDefault lighter>
                                No presentation selected
                            </TextDefault>
                            <TextDefault smaller>
                                Drag a presentation from the palette to add one
                            </TextDefault>
                        </FlexDiv>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div 
            className={`presentation-area ${isSelected ? 'selected' : ''}`}
            onClick={() => onSelect(presentation)}
        >
            <Card>
                <CardBody>
                    <FlexDiv alignCenter gap={12}>
                        <FlexDiv flexGrow={1}>
                            <Heading4>{presentation.title || presentation.type}</Heading4>
                            <TextDefault lighter>
                                {presentation.type} presentation
                            </TextDefault>
                        </FlexDiv>
                        <ThemedButton
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(presentation);
                            }}
                        />
                    </FlexDiv>
                </CardBody>
            </Card>
        </div>
    );
};

function QuestionCanvas({ 
    question, 
    selectedBlock, 
    selectedPresentation,
    onSelectBlock, 
    onSelectPresentation,
    onUpdateBlock, 
    onUpdatePresentation,
    onRemoveBlock, 
    onMoveBlock, 
    onAddBlock 
}) {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: ['block', 'presentation'],
        drop: (item, monitor) => {
            if (monitor.getItemType() === 'block' && item.blockType) {
                onAddBlock(item.blockType);
            } else if (monitor.getItemType() === 'presentation' && item.presentationType) {
                // Handle presentation drop
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }));

    const handleAddBlock = useCallback(() => {
        // Open block selection modal or dropdown
        // For now, just add a radio block as example
        onAddBlock('radio');
    }, [onAddBlock]);

    return (
        <div 
            ref={drop}
            className={`question-canvas ${isOver ? 'drop-zone' : ''}`}
        >
            <Block padding={24} gap={24}>
                <Heading4>Question Canvas</Heading4>
                
                {/* Presentation Area */}
                <div className="canvas-section">
                    <TextDefault bold>Presentation</TextDefault>
                    <PresentationArea
                        presentation={question.ui.presentation}
                        isSelected={selectedPresentation === question.ui.presentation}
                        onSelect={onSelectPresentation}
                        onUpdate={onUpdatePresentation}
                    />
                </div>

                {/* Blocks Area */}
                <div className="canvas-section">
                    <FlexDiv alignCenter gap={16}>
                        <TextDefault bold>Interactive Blocks</TextDefault>
                        <ThemedButton
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAddBlock}
                            text="Add Block"
                        />
                    </FlexDiv>
                    
                    <FlexDiv column gap={16}>
                        {question.ui.blocks.length === 0 ? (
                            <Card>
                                <CardBody>
                                    <FlexDiv column alignCenter gap={16} padding={32}>
                                        <TextDefault lighter>
                                            No blocks added yet
                                        </TextDefault>
                                        <TextDefault smaller>
                                            Drag blocks from the palette or click "Add Block"
                                        </TextDefault>
                                    </FlexDiv>
                                </CardBody>
                            </Card>
                        ) : (
                            question.ui.blocks.map((block, index) => (
                                <CanvasBlock
                                    key={block.id}
                                    block={block}
                                    index={index}
                                    isSelected={selectedBlock?.id === block.id}
                                    onSelect={onSelectBlock}
                                    onUpdate={(updates) => onUpdateBlock(block.id, updates)}
                                    onRemove={onRemoveBlock}
                                    onMove={onMoveBlock}
                                />
                            ))
                        )}
                    </FlexDiv>
                </div>
            </Block>
        </div>
    );
}

export default QuestionCanvas;
